<!-- Note, still misssing behaviour for mobile > waiting AF < -->
<template>
    <widget-wrapper
    extraClass="naHora"
    :title="t('home.widgets.naHora.title')"
    :variant="variant"
    :applyErrorState="applyErrorState"
    :hasTokenModal="showTokenModal"
    :tokenTitleAppend="t('home.widgets.naHora.tokenTitle')"
    @hideTokenChallengeModal="fullSizeOverlayLoader = false"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, submitTransfer)"
    @redirectToFeature="navigateByName(routeList.TRANSFERS_NACIONAL_NAME, { transferSelectedTabId: 'naHora' })">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader || fullSizeOverlayLoader" fullSize :fullSizeOverlay="fullSizeOverlayLoader"/>
        <!-- NaHora Form -->
        <div
        v-if="!showLoader && getAccountsList.length && !feedbackOperationData"
        class="form-wrapper"
        :class="[variant, {'mobile': getMobileAppState}]">
            <!-- Account Dropdown -->
            <dropdown-accounts
            :params="{ labelText : t('home.widgets.naHora.originAccount')}"
            :selectedOption="formData.originAccountId"
            :options="filteredAccountsList"
            :hideAmounts="hideAmounts"
            @clicked="formData.originAccountId = $event"/>
            <!-- PhoneNumber -->
            <input-generic
            v-if="!beneficiaryName"
            :class="{'add-margin': phoneNumberInputParams.errorState}"
            :focusInput="focusPhoneNumberOnMounted"
            :params="phoneNumberInputParams"
            :inputValue="formData.destinationMsisdn"
            :rightButtonIcon="getMobileAppState ? (formData.destinationMsisdn.length ? 'close' : 'contact') : (formData.destinationMsisdn.length ? 'close' : null)"
            @input="[formData.destinationMsisdn = $event, phoneNumberInputParams.errorState = false]"
            @onBlur="phoneNumberValidation"
            @rightButtonClicked="formData.destinationMsisdn.length ? formData.destinationMsisdn = '' : showContactsModal = true"/>
            <!-- This is an input, but user cant change its text -->
            <input-generic
            v-else
            class="beneficiary-name"
            rightButtonIcon="check-line"
            :params="beneficiaryNameInputParams"
            :inputValue="beneficiaryName"
            @click="showPhoneNumberValidator"/>
            <!-- Contacts Logic Wrapper -->
            <div v-if="getMobileAppState && getLastNaHoraTransfers.length" class="contacts-logic-wrapper">
                <div v-if="!hasPermission" class="no-permission-elements-wrapper">
                    <infobox-generic :label="t('home.widgets.naHora.noPermissionsNote')"/>
                    <!-- Request Permission -->
                    <button-tertiary buttonSize="small" :label="t('home.widgets.naHora.permissionBtn')" @clicked="givePermissionsHandler"/>
                </div>
                <div v-if="hasPermission && !!recentContacts.length" class="frequents-contacts-wrapper">
                    <div class="area-label">{{t('home.widgets.naHora.frequentContacts')}}</div>
                    <div v-dragscroll class="contacts-list-wrapper">
                        <div
                        v-for="contact in recentContacts"
                        :key="`contact${contact.numberToConsider}`"
                        class="contact"
                        @click="formData.destinationMsisdn = contact.numberToConsider">
                            <!-- Contact Image -->
                            <img v-if="contact.image" :src="`data:image/png;base64, ${contact.image}`" alt="Contact Image">
                            <div v-else class="acronym">{{acronymBuilder(contact.displayName)}}</div>
                            <!-- Name -->
                            <div class="name">{{contact.displayName}}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="aligner-helper" :class="{'add-gap': amountInputParams.errorState}">
                <!-- Amount -->
                <input-amount
                class="amount-input"
                :params="{ ...amountInputParams, currency: formData.currency }"
                :inputValue="formData.amount"
                @input="[formData.amount = $event, amountInputParams.errorState = false]"
                @onBlur="amountValidation"/>
                <!-- Submit Button -->
                <button-primary
                icon="arrow-right-full"
                iconPosition="right"
                buttonSize="small"
                :label="t('home.widgets.naHora.validate')"
                @clicked="validateInputs"/>
            </div>
        </div>
        <div v-if="feedbackOperationData && !showLoader" class="feedback-operation-data-wrapper">
            <!-- Feedback Operatio Component -->
            <feedback-operation iconsColorReversed variant="widget" :feedbackOperationData="feedbackOperationData"/>
            <!-- Warning Message -->
            <infobox-generic v-if="feedbackOperationData.status === 'warning'" :label="feedbackOperationData.additionalMessage"/>
            <!-- New Operation Button -->
            <button-primary
            icon="arrow-right-full"
            iconPosition="right"
            buttonSize="small"
            :label="t('home.widgets.naHora.newOperation')"
            @clicked="newOperationHandler"/>
        </div>
    </widget-wrapper>
    <!-- Error Toast -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
    <!-- Contacts Screen -->
    <modal-side-contacts
    v-if="getMobileAppState && showContactsModal"
    icon="close"
    sendType="sms"
    :triggerClose="triggerContactsModalClose"
    :title="t('modals.contacts.title')"
    :destinationValues="[formData.destinationMsisdn]"
    :showOnlyBAIContacts="true"
    @contactSelected="contactSelectedHandler"
    @hideMe="[showContactsModal = false, triggerContactsModalClose = false]"/>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import DropdownAccounts from '@/components/generic/dropdowns/accounts/Account'
import InputGeneric from '@/components/generic/inputs/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import InputAmount from '@/components/generic/inputs/Amount'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import FeedbackOperation from '@/components/generic/feedbacks/Operation'
import ModalSideContacts from '@/components/generic/modals/side/contacts/Base'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Assets */
import { ref, watch, computed } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { PHONE_NUMBER } from '@/assets/constants/masks'
import { baiPhoneNumberValidator } from '@/assets/scripts/validators'
import { acronymBuilder } from '@/assets/scripts/strings'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { contactsListMock } from '@/assets/scripts/contactsMock'
import { parseContactsList } from '@/assets/scripts/parsers/contacts'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createSmartTransferDownloadReceiptURL } from '@/network/api/smartTransfers.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@showAccountsLoader {Boolean} - prop to tell widget accounts fetch state
@applyAccountsErrorState {Boolean} - prop to tell widget accounts fetch state
@variant {String} - can be [ none (default), anchor]
@hideAmounts {Bollean} prop to hide amounts
*/
const props = defineProps({
    showAccountsLoader: Boolean,
    applyAccountsErrorState: Boolean,
    variant: String,
    hideAmounts: Boolean
})

/* mapActions & mapGetters */
const { requestLastNaHoraTransfers, requestBeneficiaryName, createSmartTransfer } = mapActions(moduleMapper.HOME)
const { getAccountsList, getLastNaHoraTransfers } = mapGetters(moduleMapper.HOME)
const { getMobileAppState, getAppThemeMode } = mapGetters()

/* Data */
const showLoader = ref(props.showAccountsLoader)
const fullSizeOverlayLoader = ref(false)
const applyErrorState = ref(props.applyAccountsErrorState)
const focusPhoneNumberOnMounted = ref(false)
const beneficiaryName = ref(null)
const successNameFetch = ref(null)
const showTokenModal = ref(false)
const contactsList = ref(null)
const recentContacts = ref([])
const hasPermission = ref(false)
const parsedAndFilteredList = ref(null)
const showContactsModal = ref(false)
const triggerContactsModalClose = ref(false)

const INITIAL_FORM_DATA = {
    originAccountId: null,
    currency: '',
    destinationMsisdn: '',
    amount: ''
}

const formData = ref(INITIAL_FORM_DATA)

watch(() => formData.value.originAccountId, (newVal, oldVal) => {
    if (!formData.value.originAccountId) return
    formData.value.currency = getAccountsList.value.find(obj => obj.id === formData.value.originAccountId).currency
})

const phoneNumberInputParams = ref({
    labelText: t('home.widgets.naHora.phoneNumber'),
    placeholder: t('home.widgets.naHora.phoneNumberPlaceholder'),
    mandatory: true,
    mask: PHONE_NUMBER,
    errorState: false,
    errorMessage: ''
})

const beneficiaryNameInputParams = ref({
    labelText: t('home.widgets.naHora.phoneNumber'),
    placeholder: t('home.widgets.naHora.phoneNumberPlaceholder'),
    mandatory: true
})

const phoneNumberValidation = () => {
    if (formData.value.destinationMsisdn.length && !baiPhoneNumberValidator(formData.value.destinationMsisdn)) {
        phoneNumberInputParams.value.errorMessage = t('phoneLengthOrError')
        phoneNumberInputParams.value.errorState = true
    } else if (formData.value.destinationMsisdn.length && baiPhoneNumberValidator(formData.value.destinationMsisdn)) {
        searchBeneficiaryName()
    }
}

const showPhoneNumberValidator = () => {
    beneficiaryName.value = null
    focusPhoneNumberOnMounted.value = true
}

const searchBeneficiaryName = async () => {
    fullSizeOverlayLoader.value = true
    beneficiaryName.value = null
    successNameFetch.value = null

    const payload = {
        operationTypeId: 32009,
		msisdn: formData.value.destinationMsisdn
    }

    try {
        const res = await requestBeneficiaryName(payload)
        beneficiaryName.value = res.data.data?.name || ''
        successNameFetch.value = true
        fullSizeOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        if (error.response.data?.code === 'invalid_msisdn') {
            phoneNumberInputParams.value.errorMessage = error.response.data.message
            phoneNumberInputParams.value.errorState = true
            fullSizeOverlayLoader.value = false
        } else errorHandler(error.response.data, searchBeneficiaryName)
    }
}

const amountInputParams = ref({
    labelText: t('home.widgets.naHora.amount'),
    mandatory: true,
    errorState: false,
    errorMessage: ''
})

const amountValidation = () => {
    if (formData.value.amount && Number(formData.value.amount) === 0) {
        amountInputParams.value.errorMessage = t('invalidAmount')
        amountInputParams.value.errorState = true
    }
}

const filteredAccountsList = computed(() => {
    return getAccountsList.value.filter(obj => obj.currency === ACCOUNT_MAIN_CURRENCY)
})

/*************************************************
*                                                *
*                  Request Logics                *
*                                                *
*************************************************/
watch(() => props.showAccountsLoader, (newVal, oldVal) => {
    if (!props.showAccountsLoader && getAccountsList.value.length) {
        /* Setting Initial Selected Account */
		formData.value.originAccountId = getAccountsList.value.find(obj => obj.default).id
        showLoader.value = false
        if (getMobileAppState.value) fetchLastNaHoraTransfers()
    }
})

watch(() => props.applyAccountsErrorState, (newVal, oldVal) => {
    if (props.applyAccountsErrorState) applyErrorState.value = true
})

const fetchLastNaHoraTransfers = async () => {
    showLoader.value = true

	try {
        await requestLastNaHoraTransfers({ type: 32009 })
        initialCheckOnContacts()
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
	}
}

/*************************************************
*                                                *
*               Submit Transfer Logic            *
*                                                *
*************************************************/
const validateInputs = () => {
    if (!formData.value.destinationMsisdn.length || !baiPhoneNumberValidator(formData.value.destinationMsisdn) || !successNameFetch.value) {
        phoneNumberInputParams.value.errorState = true

        if (!formData.value.destinationMsisdn.length || !baiPhoneNumberValidator(formData.value.destinationMsisdn)) {
            phoneNumberInputParams.value.errorMessage = !formData.value.destinationMsisdn.length ? t('mandatoryFieldError') : t('phoneLengthError')
        }
    }

    if (!Number(formData.value.amount)) {
        amountInputParams.value.errorState = true
        amountInputParams.value.errorMessage = !formData.value.amount ? t('mandatoryFieldError') : t('invalidAmount')
    }

    if (!formData.value.destinationMsisdn.length || (formData.value.destinationMsisdn.length !== 9 && formData.value.destinationMsisdn.length !== 14) ||
        !Number(formData.value.amount)) {
        return
    }

    if (phoneNumberInputParams.value.errorState || amountInputParams.value.errorState) {
        return
    }

    submitTransfer()
}

const feedbackOperationData = ref(null)
const submitTransfer = async () => {
    fullSizeOverlayLoader.value = true

	try {
        await createSmartTransfer(formData.value)
        showTokenModal.value = true
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, submitTransfer)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const successHandler = data => {
    const successOperationId = data.data.smartTransfer.operationId

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        additionalMessage: data.additionalMessage,
        buttonsData: data.errorLevel === 'warning' ? null : {
            emailAction: 'sendSmartTransferSubmissionEmailNotification',
            smsAction: 'sendSmartTransferSubmissionSmsNotification',
            sendPayload: { operationID: successOperationId },
            downloadUrl: createSmartTransferDownloadReceiptURL({ params: { operationID: successOperationId } }),
            operationId: successOperationId
        }
    }
}

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor)

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showTokenModal.value = false
    fullSizeOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/* Clearing form data for new operation */
const clearFormData = () => {
    formData.value = INITIAL_FORM_DATA
    formData.value.originAccountId = getAccountsList.value.find(obj => obj.default).id
    feedbackOperationData.value = null
}

const newOperationHandler = () => {
    clearFormData()
    fetchLastNaHoraTransfers()
}

/*************************************************
*                                                *
*                MOBILE CONTACTS lOGIC           *
*                                                *
*************************************************/
const noContactsImage = computed(() => {
    return getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
})

const initialCheckOnContacts = () => {
    /* If using mocks set contactList as mock file and set permissions to true */
    if (process.env.VUE_APP_MOCKS === 'true') {
        contactsList.value = contactsListMock
        hasPermission.value = true
        getParsedAndFilteredContacts()
    } else {
        // Check if has permission to access contacts
        window.ContactsX.hasPermission((success) => {
            console.log('[HAS PERMISSIONS SUCCESS]', success)
            hasPermission.value = success.read
            /* Get contacts list */
            getContactsList()
        }, (error) => {
            console.log('[HAS PERMISSIONS ERROR]', error)
        })
    }
}

const givePermissionsHandler = () => {
    // Request permission to access contacts
    window.ContactsX.requestPermission((success) => {
        console.log('[REQUEST PERMISSIONS SUCCESS]', success)
        hasPermission.value = success.read

        // Get contacts list
        getContactsList()
    }, (error) => {
        console.log('[REQUEST PERMISSIONS ERROR]', error)
    })
}

const getParsedAndFilteredContacts = () => {
    parsedAndFilteredList.value = parseContactsList(contactsList.value, [])

    recentContacts.value = getLastNaHoraTransfers.value.reduce((acc, transfer) => {
        const phoneNumber = transfer.parameters.find(obj => obj.parameterName === 'destinationMsisdn')?.value

        parsedAndFilteredList.value.forEach(letter => {
            letter.contacts.forEach(contact => {
                contact.phoneNumbers.forEach(number => {
                    if (number.value === phoneNumber && !acc.find(obj => obj.numberToConsider === number.value)) {
                        acc.push({
                            ...contact,
                            numberToConsider: number.value,
                            displayName: `${contact.firstName} ${contact.familyName.slice(0, 1)}.`,
                            image: contact.imageData
                        })
                    }
                })
            })
        })
        return acc
    }, [])
}

const getContactsList = () => {
    if (!hasPermission.value) return

    // Get contacts list
    window.ContactsX.find((success) => {
        console.log('[GET CONTACTS SUCCESS]', success)
        contactsList.value = success
        // Parse and filter contacts list
        getParsedAndFilteredContacts()
    }, (error) => {
        console.log('[GET CONTACTS ERROR]', error)
    }, {
        fields: {
            phoneNumbers: true,
            imageData: true
        }
    })
}

const destinationValues = ref([''])
const contactSelectedHandler = (data) => {
    const selectedContact = {
        isPhoneContact: true,
        id: data.contact.id,
        firstName: data.contact.firstName,
        middleName: data.contact.middleName,
        familyName: data.contact.familyName,
        imageData: data.contact.imageData ? data.contact.imageData : false,
        contactData: data.contactData,
        errorState: false
    }

    destinationValues.value[0] = selectedContact
    formData.value.destinationMsisdn = selectedContact.contactData
    triggerContactsModalClose.value = true
}
</script>

<style lang="scss" scoped>
.form-wrapper {
    @include commonEnteringAnimation;
    width: 100%;
    padding: 16px 24px 0 24px;

    display: flex;
    flex-direction: column;

    .dropdown-wrapper {
        margin-bottom: 16px;
    }

    .beneficiary-name {
        cursor: pointer;

        :deep(.input-content) {
            border-color: var(--positive);
            pointer-events: none;
        }

        :deep(.button-quaternary) {
            pointer-events: none;

            .icon .change-color-fill {
                fill: var(--positive);
            }
        }
    }

    .input-elements-container {
        min-height: 88px;
        margin-bottom: 8px;
    }

    .contacts-logic-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-bottom: 16px;

        .no-permission-elements-wrapper {
            width: calc(100% - 32px);
            margin: 0 auto;

            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .frequents-contacts-wrapper {
            width: 100%;
            display: flex;
            flex-direction: column;

            .area-label {
                width: calc(100% - 32px);
                margin: 0 auto 8px auto;

                @include H7-Regular;
                color: var(--secondary-dark-1);
            }

            .contacts-list-wrapper {
                width: 100%;
                height: 86px;
                padding: 0 16px;

                display: flex;
                gap: 16px;

                overflow: unset;

                &.no-contacts {
                    height: 56px;

                    .contact {
                        height: 100%;
                    }
                }

                .contact {
                    width: 56px;
                    height: 76px;

                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;

                    img, .acronym {
                        width: 56px;
                        height: 56px;

                        border-radius: 100%;
                    }

                    .acronym {
                        @include H4-Medium;
                        text-align: center;
                        line-height: 56px;

                        background-color: var(--secondary-light-4);
                        border: 2px solid var(--secondary-light-2);
                    }

                    .name {
                        width: 100%;

                        @include H7-Medium;
                        color: var(--secondary-dark-2);
                        text-align: center;
                    }
                }
            }
        }

    }

    .aligner-helper {
        width: 100%;

        display: flex;
        gap: 16px;

        .input-elements-container {
            margin-bottom: 0;
        }

        .button-primary {
            width: fit-content;
            margin-top: 24px;
        }
    }

    &.anchor {
        .aligner-helper {
            width: 100%;
            flex-direction: column;
            gap: 8px;

            .button-primary {
                width: 100%;
                margin-top: 0;
            }
        }
    }

    &.mobile {
        padding: 16px 0 0 0;

        .dropdown-wrapper, .input-elements-container {
            width: calc(100% - 32px);
            margin-left: auto;
            margin-right: auto;
        }

        .dropdown-wrapper {
            margin-bottom: 16px;
        }

        .input-elements-container {
            margin-bottom: 0;

            &.add-margin {
                margin-bottom: 16px;
            }
        }

        .aligner-helper {
            padding: 0;
            flex-direction: column;
            gap: 0;

            &.add-gap {
                gap: 16px;
            }

            .button-primary {
                width: calc(100% - 32px);
                margin-left: auto;
                margin-right: auto;
                margin-top: 0;
            }
        }
    }
}

.feedback-operation-data-wrapper {
    width: 100%;
    height: 100%;
    padding: 0 16px;

    display: flex;
    flex-direction: column;
    gap: 16px;

    overflow: hidden;

    .feedback-operation-wrapper {
        height: 100%;
    }

    .infobox-generic-wrapper {
        margin-bottom: 16px;
        flex-shrink: 0;
    }

    .button-primary {
        width: 100%;
        flex-shrink: 0;
    }
}
</style>
